import { Component,  OnInit, OnDestroy, ElementRef, ViewChild, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthorizePaymentService } from 'src/app/shared/services/api/authorize-payment.service';
import { getTokenPage, getTokenPageResponse, NotifyTokenAddRequest, NotifyTokenAddResponse } from 'src/app/shared/models/authorizeNet.model';
import { environment } from 'src/environments/environment';
import { ApiEndpoints } from 'src/config/api-endpoints';

// Utility functions (assuming you still use this helper in the final environment)
declare global {
  interface Window {
    AuthorizeNetIFrame?: any;
  }
}


@Component({
  selector: 'app-hosted-payment-popup',
  templateUrl: './hosted-payment-popup.component.html',
  styleUrls: ['./hosted-payment-popup.component.scss']
})


export class HostedPaymentPopupComponent implements OnInit, OnDestroy {

  loading: boolean = true;
  systemError: boolean = false;
  
  @ViewChild('addPaymentIframe') addPaymentIframe: ElementRef | undefined;
  
  // State for redirection detection
  private subscription: Subscription = new Subscription();
    private iframeWasSubmitted: boolean = false; 
  private awaitingExternalRedirect: boolean = false;

  paymentSuccessful: boolean = false;
  private isModalOpen: boolean = true; 

  private communicatorUrl: string = `${environment.walletAPPUrl}/assets/web/hostedPaymentIFrameCommunicatorUrl.html`;

  private userId = sessionStorage.getItem('PPIUserID');
  private customerId = sessionStorage.getItem('PPICustomerID');

  constructor(
    public dialogRef: MatDialogRef<HostedPaymentPopupComponent>,
    private router: Router,
    private authorizePaymentService: AuthorizePaymentService,
    private sanitizer: DomSanitizer
  ) {
        // 🚨 MODIFICATION: We keep the router listener, but it only triggers success detection.
    // It's not the primary mechanism anymore, but a cleanup measure.
    this.subscription.add(this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // If the page navigates away to home, treat it as success detection (fallback)
      if (this.iframeWasSubmitted && (event.url === '/home' || event.url === '/')) {
        this.displaySuccessAndNotifyServer(true); // Treat external redirect as success
      }
    }));
  }

  ngOnInit() {
    this.loadHostedPaymentForm();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
  
  // ✅ FIX: Close icon function. Closes the dialog and passes back 'cancel' result.
  // This function is triggered when the user clicks the 'X' button.
  closeModal(result: 'success' | 'cancel'): void {
    this.isModalOpen = false;
    
    // 🚨 CRITICAL LOGIC: If the user manually closes the modal, and the form was successfully
    // submitted (iframeWasSubmitted is true), we assume the card was saved
    // and the redirection failed due to the CSP block. We then manually fire the success routine.
    if (result === 'cancel' && this.iframeWasSubmitted && !this.paymentSuccessful) {
        console.warn('Manual closure detected after form submission. Triggering delayed success logic.');
        // We trigger the success logic which handles API call and final dialog close
        this.displaySuccessAndNotifyServer(true); 
        return; // Halt here, displaySuccessAndNotifyServer will close dialog
    }

    // Hide the iframe element explicitly
    const ifrm = window.document.getElementById('add_payment') as HTMLIFrameElement;
    if (ifrm) {
        ifrm.style.visibility = 'hidden';
        ifrm.style.display = 'none';
    }

    // Default cancellation path (user closed before submitting)
    this.dialogRef.close(result);
  }

  // 🚨 MODIFIED: Now accepts a parameter to trigger success or handle cleanup
  private displaySuccessAndNotifyServer(triggerServerNotify: boolean): void {
    this.iframeWasSubmitted = false; // Reset the flag

    // 1. Hide the Iframe container immediately
    const ifrm = window.document.getElementById('add_payment') as HTMLIFrameElement;
    if (ifrm) {
        ifrm.style.display = 'none';
        ifrm.style.visibility = 'hidden';
    }

    this.paymentSuccessful = true; // Show success overlay 
    
    if (triggerServerNotify) {
       const rawWalletID = sessionStorage.getItem('WalletID') || '';
        const cleanWalletID = rawWalletID.replace(/\\?\"/g, ''); // Removes all quotes and escaped quotes
        // 2. Manually trigger the server notification (notifyTokenAdd)
        const notifyRequest: NotifyTokenAddRequest = {
            walletID: cleanWalletID,
            sessionID: sessionStorage.getItem('SessionID') || '',
            userID: this.userId,
        };
        
        this.authorizePaymentService.notifyTokenAdd(notifyRequest).subscribe({
            next: (response: NotifyTokenAddResponse) => {
                if (response.success) {
                    // 3. Final closure after API confirms
                    setTimeout(() => this.dialogRef.close('success'), 1500); 
                } else {
                    console.error('DWNotifyTokenAdd API failed after successful submission.');
                    setTimeout(() => this.dialogRef.close('cancel'), 1500); 
                }
            },
            error: (err) => {
                console.error('Error calling DWNotifyTokenAdd API:', err);
                setTimeout(() => this.dialogRef.close('cancel'), 1500);
            }
        });
    } else {
        // If notify server wasn't triggered (e.g., external redirect was not detected), just close
        this.dialogRef.close('cancel');
    }
  }

  private loadHostedPaymentForm() {
    const walletID = sessionStorage.getItem('WalletID')?.replace(/"/g, '') || '';
    const sessionID = sessionStorage.getItem('SessionID') || '';
    const userID = sessionStorage.getItem('PPIUserID') || '';

    const getNewTokenPageRequest: getTokenPage = { walletID, sessionID, userID };

    this.authorizePaymentService.getNewTokenPage(getNewTokenPageRequest).subscribe({
      next: (response: getTokenPageResponse) => {
        this.loading = false;
        if (response.success) {
          const formToken = response.data.addForm.match(/value='([^']*)'/)?.[1];
          if (formToken) {
            this.loadIframeWithForm(formToken);
            this.isModalOpen = true; // Open the modal on success
          } else {
            this.systemError = true;
          }
        } else {
          this.systemError = true;
        }
      },
      error: (err) => {
        this.loading = false;
        this.systemError = true;
      }
    });
  }

  private loadIframeWithForm(formToken: string) {
    const form = document.createElement('form');
    form.setAttribute('id', 'send_token');
    form.setAttribute('method', 'post');
    form.setAttribute('action', ApiEndpoints.authorizenetaddpayment);
    form.setAttribute('target', 'authorizeNetIframe'); // Target the iframe

    const communicatorUrl = `${environment.walletAPPUrl}/assets/web/hostedPaymentIFrameCommunicatorUrl.html`;

    // Add required hidden inputs
    form.innerHTML = `
      <input type="hidden" name="token" value="${formToken}" />
      <input type="hidden" name="hostedPaymentIFrameCommunicatorUrl" value="${communicatorUrl}" />
    `;

    document.body.appendChild(form);
    
    // ✅ CRITICAL: Set flag just before submitting the form
    this.iframeWasSubmitted = true;
    
    if (this.addPaymentIframe?.nativeElement) {
      this.addPaymentIframe.nativeElement.style.visibility = 'visible';
      this.addPaymentIframe.nativeElement.style.display = 'block';
    }

    form.submit();
    document.body.removeChild(form);
  }
}